import { useId, useMemo, useState } from 'react';
import { faNumber } from '../lib/format';

/**
 * The chart kit.
 *
 * Hand-written SVG rather than a charting library, for the same reason the PDF
 * writer is hand-written: everything here has to read right-to-left in Persian
 * digits, and every general-purpose library wants to be talked out of a
 * left-to-right axis and Latin numerals afterwards.
 *
 * Three rules the whole file obeys:
 *   1. **Time runs right to left.** Period 0 sits at the right edge. This is not
 *      decoration — in an RTL statement the earliest column is the rightmost,
 *      and a chart that disagrees with the table beside it is read as a
 *      different period, not a different layout.
 *   2. **A missing value breaks the line.** Nulls are never interpolated and
 *      never plotted as zero; the stroke stops and restarts. A month the
 *      pipeline could not compute must not draw a collapse to zero.
 *   3. **No colour is hard-coded.** Series colours come from CSS custom
 *      properties so they flip with the theme.
 */

const SERIES_VARS = ['--series-1', '--series-2', '--series-3', '--series-4', '--series-5', '--series-6'];

export function seriesColor(index: number): string {
  return `var(${SERIES_VARS[index % SERIES_VARS.length]})`;
}

export interface TrendPoint {
  key: string;
  labelFa: string;
}

export interface TrendSeries {
  key: string;
  labelFa: string;
  /** Indexed by period key. A key that is absent is a gap, not a zero. */
  points: Record<string, number | null>;
}

/** Rounds an axis maximum up to something a person would have chosen. */
function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
}

export function TrendChart({
  periods,
  series,
  format,
  height = 240,
  caption,
}: {
  periods: TrendPoint[];
  series: TrendSeries[];
  /** How a y value is written out — the caller owns units and currency. */
  format: (value: number) => string;
  height?: number;
  caption?: string;
}) {
  const gradientId = useId();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const shown = series.filter((item) => !hidden.has(item.key));

  const width = 720;
  // The right gutter holds the value axis, which in an RTL chart sits on the
  // same side as the first period's label — 54 was not enough and «فروردین»
  // ran straight into «۰».
  const padding = { top: 16, right: 86, bottom: 34, left: 30 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const bounds = useMemo(() => {
    const values = shown.flatMap((item) => periods
      .map((period) => item.points[period.key])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value)));
    if (values.length === 0) return null;
    const max = Math.max(...values, 0);
    const min = Math.min(...values, 0);
    // A series that never goes negative gets a floor of zero: starting the axis
    // at the smallest value exaggerates every wobble into a cliff.
    const top = niceCeil(max === 0 && min === 0 ? 1 : max);
    const bottom = min < 0 ? -niceCeil(Math.abs(min)) : 0;
    return { top, bottom };
  }, [shown, periods]);

  if (periods.length === 0 || series.length === 0 || !bounds) {
    return <p className="hint">برای رسم روند، هنوز دوره‌ای با داده محاسبه‌شده وجود ندارد.</p>;
  }

  const span = bounds.top - bounds.bottom || 1;
  // Right to left: index 0 is pinned to the right edge of the plot.
  const xOf = (index: number) => padding.left + plotWidth
    - (periods.length === 1 ? plotWidth / 2 : (index / (periods.length - 1)) * plotWidth);
  const yOf = (value: number) => padding.top + plotHeight - ((value - bounds.bottom) / span) * plotHeight;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => bounds.bottom + fraction * span);

  /** Contiguous runs of real values, so a gap ends the stroke instead of jumping it. */
  const runsOf = (item: TrendSeries): Array<Array<{ x: number; y: number }>> => {
    const runs: Array<Array<{ x: number; y: number }>> = [];
    let current: Array<{ x: number; y: number }> = [];
    periods.forEach((period, index) => {
      const value = item.points[period.key];
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        if (current.length) runs.push(current);
        current = [];
        return;
      }
      current.push({ x: xOf(index), y: yOf(value) });
    });
    if (current.length) runs.push(current);
    return runs;
  };

  // Labelling every month overlaps at this width; a fixed stride keeps the ones
  // that are drawn readable and evenly spaced.
  const labelStride = Math.ceil(periods.length / 8);

  const summary = shown.map((item) => {
    const values = periods
      .map((period) => item.points[period.key])
      .filter((value): value is number => typeof value === 'number');
    if (!values.length) return `${item.labelFa}: بدون داده`;
    return `${item.labelFa}: از ${format(values[0])} تا ${format(values[values.length - 1])}`;
  }).join('؛ ');

  return (
    <div className="trend">
      <svg
        className="trend__canvas"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${caption ? `${caption}. ` : ''}${summary}`}
      >
        <defs>
          <linearGradient id={`${gradientId}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--series-1)" stopOpacity="0.16" />
            <stop offset="100%" stopColor="var(--series-1)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={padding.left} x2={width - padding.right}
              y1={yOf(tick)} y2={yOf(tick)}
              className={tick === 0 && bounds.bottom < 0 ? 'trend__zero' : 'trend__grid'}
            />
            {/* The value axis sits on the right, where an RTL reader starts.
                Anchored to the far edge of the viewBox rather than to the edge
                of the plot: `text-anchor` resolves against the inherited
                direction, and under `direction: rtl` a "start"-anchored label
                grows LEFTWARD — from the plot edge that ran the tick values
                back across the chart and over the line itself. Pinned here it
                grows left into the gutter, which is where the gutter is. */}
            <text x={width - 6} y={yOf(tick) + 4} className="trend__tick" textAnchor="start">
              {format(tick)}
            </text>
          </g>
        ))}

        {periods.map((period, index) => (index % labelStride === 0 ? (
          <text
            key={period.key}
            x={xOf(index)}
            y={height - 10}
            className="trend__tick"
            // Middle throughout: it is the one anchor whose meaning does not
            // flip with the text direction, and the gutters are wide enough
            // for the outermost labels to sit centred without clipping.
            textAnchor="middle"
          >
            {period.labelFa.split(' ')[0]}
          </text>
        ) : null))}

        {shown.length === 1 && runsOf(shown[0]).map((run, runIndex) => (
          <path
            key={`fill-${runIndex}`}
            d={`M ${run[0].x} ${yOf(Math.max(bounds.bottom, 0))} ${run.map((point) => `L ${point.x} ${point.y}`).join(' ')} L ${run[run.length - 1].x} ${yOf(Math.max(bounds.bottom, 0))} Z`}
            fill={`url(#${gradientId}-fill)`}
            stroke="none"
          />
        ))}

        {shown.map((item) => {
          const color = seriesColor(series.findIndex((candidate) => candidate.key === item.key));
          return (
            <g key={item.key}>
              {runsOf(item).map((run, runIndex) => (
                <path
                  key={runIndex}
                  className="trend__line"
                  d={run.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')}
                  stroke={color}
                />
              ))}
              {runsOf(item).flat().map((point, index) => (
                <circle key={index} cx={point.x} cy={point.y} r={3} fill={color} className="trend__dot" />
              ))}
            </g>
          );
        })}
      </svg>

      {series.length > 1 && (
        <div className="trend__legend">
          {series.map((item, index) => {
            const off = hidden.has(item.key);
            return (
              <button
                key={item.key}
                type="button"
                className={off ? 'trend__key trend__key--off' : 'trend__key'}
                aria-pressed={!off}
                onClick={() => setHidden((current) => {
                  const next = new Set(current);
                  if (next.has(item.key)) next.delete(item.key);
                  // Hiding the last visible series would leave an empty frame
                  // with no way back, so the final one stays on.
                  else if (series.length - next.size > 1) next.add(item.key);
                  return next;
                })}
              >
                <span className="trend__swatch" style={{ background: seriesColor(index) }} />
                {item.labelFa}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * What a score is made of.
 *
 * Shown under every ring, because «۸۷» on its own tells an accountant nothing
 * about which band cost the other ۱۳ — and the note is the part that says
 * where to go and fix it.
 */
export function ScoreBands({ contributions }: { contributions: Array<{ key: string; labelFa: string; points: number; maxPoints: number; note: string }> }) {
  if (contributions.length === 0) return null;
  return (
    <div className="score-bands">
      {contributions.map((band) => (
        <div key={band.key} className="score-band">
          <div className="score-band__head">
            <span>{band.labelFa}</span>
            <b>{faNumber(band.points)} از {faNumber(band.maxPoints)}</b>
          </div>
          <div className="score-band__meter">
            <span style={{ width: `${Math.max(0, Math.min(100, (band.points / band.maxPoints) * 100))}%` }} />
          </div>
          <p className="score-band__note">{band.note}</p>
        </div>
      ))}
    </div>
  );
}

export interface DonutSlice {
  key: string;
  labelFa: string;
  value: number;
}

/**
 * Composition of one total — «خلاصه ترازنامه» in the design.
 *
 * Negative slices are dropped rather than mirrored: a donut shows parts of a
 * whole, and a negative part of a whole is not a thing a reader can see. The
 * caller is expected to have chosen figures that genuinely sum.
 */
export function Donut({
  slices,
  centerLabel,
  centerValue,
  format,
}: {
  slices: DonutSlice[];
  centerLabel: string;
  centerValue: string;
  format: (value: number) => string;
}) {
  const usable = slices.filter((slice) => Number.isFinite(slice.value) && slice.value > 0);
  const total = usable.reduce((sum, slice) => sum + slice.value, 0);
  if (total <= 0) return <p className="hint">ترکیب این دوره قابل نمایش نیست.</p>;

  const size = 168;
  const radius = 62;
  const thickness = 22;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="donut">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="donut__canvas"
        role="img"
        aria-label={`${centerLabel}: ${centerValue}. ${usable.map((slice) => `${slice.labelFa} ${faNumber((slice.value / total) * 100, 1)}٪`).join('، ')}`}
      >
        <g transform={`translate(${size / 2} ${size / 2}) rotate(-90)`}>
          {usable.map((slice, index) => {
            const length = (slice.value / total) * circumference;
            const dash = `${length} ${circumference - length}`;
            const element = (
              <circle
                key={slice.key}
                r={radius}
                fill="none"
                strokeWidth={thickness}
                stroke={seriesColor(index)}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return element;
          })}
        </g>
        <text x={size / 2} y={size / 2 - 2} className="donut__value" textAnchor="middle">{centerValue}</text>
        <text x={size / 2} y={size / 2 + 16} className="donut__label" textAnchor="middle">{centerLabel}</text>
      </svg>
      <ul className="donut__legend">
        {usable.map((slice, index) => (
          <li key={slice.key}>
            <span className="donut__swatch" style={{ background: seriesColor(index) }} />
            <span className="donut__name">{slice.labelFa}</span>
            <b>{format(slice.value)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A ratio's shape over the periods, sized to sit inside a table row.
 *
 * Purely indicative and labelled as such: it carries no axis, so it says
 * «this went up» and never «this went up to here».
 */
export function Sparkline({ values, labelFa }: { values: Array<number | null>; labelFa: string }) {
  const real = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (real.length < 2) return <span className="sparkline sparkline--empty" aria-hidden="true" />;

  const width = 88;
  const height = 24;
  const max = Math.max(...real);
  const min = Math.min(...real);
  const span = max - min || 1;
  const step = values.length === 1 ? 0 : width / (values.length - 1);

  const runs: string[] = [];
  let current: string[] = [];
  values.forEach((value, index) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      if (current.length) runs.push(current.join(' '));
      current = [];
      return;
    }
    // Right to left, matching the trend chart above it.
    const x = width - index * step;
    const y = height - 2 - ((value - min) / span) * (height - 4);
    current.push(`${current.length === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
  });
  if (current.length) runs.push(current.join(' '));

  const rising = real[real.length - 1] >= real[0];
  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      // Direction is stated in words and drawn in ONE neutral colour.
      // Green-for-up would be a verdict, and it would be the wrong one half the
      // time: a falling نسبت بدهی به دارایی is a company deleveraging, and
      // painting that red tells an accountant something untrue.
      aria-label={`روند ${labelFa}: ${rising ? 'صعودی' : 'نزولی'}`}
    >
      {runs.map((run, index) => (
        <path key={index} d={run} fill="none" strokeWidth={1.6} className="sparkline__line" />
      ))}
    </svg>
  );
}

/**
 * A 0–100 score as a ring — «سلامت مالی» and «کیفیت داده».
 *
 * `coverage` is drawn as a lighter arc behind the score, because a score
 * standing on half its inputs must not look like one standing on all of them.
 * `null` renders as «—», never as an empty ring, which would read as zero.
 */
export function ScoreRing({
  score,
  coverage,
  labelFa,
  captionFa,
  size = 148,
}: {
  score: number | null;
  coverage?: number;
  labelFa: string;
  captionFa?: string;
  size?: number;
}) {
  const radius = size / 2 - 14;
  const circumference = 2 * Math.PI * radius;
  const clamped = score === null ? 0 : Math.max(0, Math.min(100, score));
  const tone = score === null ? 'unknown' : clamped >= 75 ? 'good' : clamped >= 50 ? 'fair' : 'poor';

  return (
    <div className={`score-ring score-ring--${tone}`}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        role="img"
        aria-label={score === null ? `${labelFa}: محاسبه نشده` : `${labelFa}: ${faNumber(clamped)} از ۱۰۰`}
      >
        <g transform={`translate(${size / 2} ${size / 2}) rotate(-90)`}>
          <circle r={radius} fill="none" strokeWidth={11} className="score-ring__track" />
          {coverage !== undefined && coverage < 1 && (
            <circle
              r={radius} fill="none" strokeWidth={11} className="score-ring__coverage"
              strokeDasharray={`${circumference * coverage} ${circumference}`}
              strokeLinecap="round"
            />
          )}
          {score !== null && (
            <circle
              r={radius} fill="none" strokeWidth={11} className="score-ring__value"
              strokeDasharray={`${(circumference * clamped) / 100} ${circumference}`}
              strokeLinecap="round"
            />
          )}
        </g>
        <text x={size / 2} y={size / 2 + 2} className="score-ring__score" textAnchor="middle">
          {score === null ? '—' : faNumber(clamped)}
        </text>
        <text x={size / 2} y={size / 2 + 22} className="score-ring__unit" textAnchor="middle">
          {score === null ? 'محاسبه نشده' : 'از ۱۰۰'}
        </text>
      </svg>
      <div className="score-ring__meta">
        <strong>{labelFa}</strong>
        {captionFa && <small>{captionFa}</small>}
      </div>
    </div>
  );
}
